import { client } from "./supabase";
import { escribirEventosSaldo } from "./vincularFacturasCuotas";

// Giro efectivo de una operación: si el proveedor ya depositó (giro_depositado seteado),
// ese depósito real es la verdad; si es null, se usa el giro_a_recibir modelado (proyección).
// Chokepoint: gate de vinculación y listado de candidatas leen SIEMPRE por acá.
export const getGiroEfectivo = (op) =>
  op.giro_depositado != null ? Number(op.giro_depositado) : Number(op.giro_a_recibir || 0);

// Techo de sanidad del costo de factoring: ningún costo real (interés + comisión) llega al
// 30% del capital adelantado. Si capital_adelantado − giro_depositado supera ese techo, el
// giro no fue consumido por costo sino NETEADO contra principal viejo (refinanciamiento /
// rollover). Caso real: op 17 folio 432 (rollover de folio 417) → "costo" $4.344.421 = 66%
// del capital. En ese caso el giro_depositado es correcto y trazable a banco, pero interpretarlo
// como costo es falso; se cae al modelado y se marca la op como neteada.
const TECHO_COSTO_FACTORING = 0.3;

// True cuando el giro real está por debajo de lo razonable (neteo/rollover), no cuando
// simplemente difiere del modelado. Solo aplica si hay depósito real cargado.
export const esNeteada = (op) => {
  if (op.giro_depositado == null) return false;
  const capital = Number(op.capital_adelantado || 0);
  if (capital <= 0) return false;
  const costoEfectivo = capital - Number(op.giro_depositado);
  return costoEfectivo > TECHO_COSTO_FACTORING * capital;
};

// Costo efectivo que retuvo el factor sobre el adelanto. Con depósito real, el costo real
// = capital_adelantado − giro_depositado (ya incluye interés + comisión, sea lo que haya
// cobrado el proveedor), salvo que dispare el techo de sanidad (op neteada → modelado).
// Sin depósito, se usa el costo modelado (costo_financiero + comisión).
// Chokepoint: el card de costo total del ER de factoring lee SIEMPRE por acá.
export const getCostoEfectivo = (op) => {
  const modelado = Number(op.costo_financiero || 0) + Number(op.comision_proveedor || 0);
  if (op.giro_depositado == null) return modelado;
  const capital = Number(op.capital_adelantado || 0);
  const costoEfectivo = capital - Number(op.giro_depositado);
  // Neteo/rollover: el "costo" supera el techo → no es costo real, caer al modelado.
  if (capital > 0 && costoEfectivo > TECHO_COSTO_FACTORING * capital) return modelado;
  return costoEfectivo;
};

export const getProveedoresFactoring = async (pais) => {
  const { data, error } = await client
    .from("proveedores_factoring")
    .select("*")
    .eq("pais", pais)
    .eq("activo", true)
    .order("nombre");
  if (error) throw error;
  return data || [];
};

export const getOperacionesFactoring = async (pais, filters = {}) => {
  let query = client
    .from("operaciones_factoring")
    .select(
      `
      *,
      factura:facturas_sii_emitidas(folio, razon_social_receptor, monto_total, fecha_emision, cliente_id),
      proveedor:proveedores_factoring(nombre, tipo, tasa_habitual),
      moras:operaciones_factoring_mora!operaciones_factoring_mora_operacion_factoring_id_fkey(id, monto_mora, estado),
      facturas_vinculadas:operaciones_factoring_facturas(
        factura_sii_emitida_id,
        monto_factura,
        factura:facturas_sii_emitidas(folio, razon_social_receptor)
      ),
      movimientos_vinculados:operaciones_factoring_movimientos(
        id, tipo,
        movimiento:movimientos_bancarios(id, fecha, monto)
      )
    `
    )
    .eq("pais", pais)
    .order("fecha_operacion", { ascending: false });

  if (filters.estado) query = query.eq("estado", filters.estado);
  if (filters.fechaDesde) query = query.gte("fecha_operacion", filters.fechaDesde);

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
};

export const crearOperacionFactoring = async (operacion) => {
  // Forzamos estado="activa" — la DB tiene default='activo' (masculino) que el código
  // de la página filtra como activa (femenino). Ver migración 20260509_factoring_campos.sql
  // que ajusta el default; este override garantiza consistencia hasta que se aplique.
  const { data, error } = await client
    .from("operaciones_factoring")
    .insert({ ...operacion, estado: "activa" })
    .select()
    .single();
  if (error) throw error;

  const { error: errorEstado } = await client
    .from("facturas_sii_emitidas")
    .update({ estado_factoring: "cedida" })
    .eq("id", operacion.factura_sii_emitida_id);
  if (errorEstado) throw errorEstado;

  return data;
};

export const actualizarOperacionFactoring = async (id, cambios) => {
  const { data, error } = await client
    .from("operaciones_factoring")
    .update({ ...cambios, updated_at: new Date().toISOString() })
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;
  return data;
};

// Eliminar definitivamente una operación. Solo procede si ya está anulada — la
// constraint .eq("estado","anulada") en el DELETE evita borrar operaciones activas
// o liquidadas por error.
export const eliminarOperacionFactoring = async (id) => {
  const { error } = await client.from("operaciones_factoring").delete().eq("id", id).eq("estado", "anulada");
  if (error) throw error;
};

/**
 * Anula una operación de factoring de forma ATÓMICA vía el RPC `anular_operacion_factoring`
 * (ver migración 20260701_anular_operacion_factoring_rpc.sql), espejo inverso del RPC de liquidar.
 *
 * El RPC, en una sola transacción:
 *   1. UPDATE operaciones_factoring → estado='anulada'
 *   2. Revierte estado_factoring=NULL Y facturafechacobro=NULL en TODAS las facturas de la op
 *      (FK directo `factura_sii_emitida_id` + puente `operaciones_factoring_facturas`,
 *      deduplicadas). Cubre ops multi-factura y limpia la fecha de cobro stale — corrige la
 *      asimetría del anular anterior. La factura vuelve a "Por cobrar" en FlujoCajaV2.
 *
 * @param {number} operacionId
 */
export const anularOperacionFactoring = async (operacionId, { supabaseClient = client } = {}) => {
  const { data, error } = await supabaseClient.rpc("anular_operacion_factoring", { p_op_id: operacionId });
  if (error) throw error;

  // FASE 4 dual-write (best-effort): revertir los eventos de esta operación en el libro (delete puro).
  try {
    await escribirEventosSaldo(supabaseClient, { origen_tipo: "operacion_factoring", origen_id: operacionId }, []);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(
      "[dual-write aplicaciones_saldo · factoring anular] best-effort, no rompe el flujo:",
      e?.message || e
    );
  }
  return data;
};

export const getMorasOperacion = async (operacionFactoringId) => {
  const { data, error } = await client
    .from("operaciones_factoring_mora")
    .select("*")
    .eq("operacion_factoring_id", operacionFactoringId)
    .order("fecha_notificacion", { ascending: false });
  if (error) throw error;
  return data || [];
};

export const crearMoraFactoring = async ({
  operacion_factoring_id,
  pais,
  monto_mora,
  fecha_notificacion,
  notas,
  created_by,
}) => {
  const { data, error } = await client
    .from("operaciones_factoring_mora")
    .insert([
      {
        operacion_factoring_id,
        pais,
        monto_mora,
        fecha_notificacion,
        notas,
        created_by,
        estado: "pendiente",
      },
    ])
    .select()
    .single();
  if (error) throw error;
  return data;
};

export const pagarMoraFactoring = async (moraId, fechaPagoMora) => {
  const { data, error } = await client
    .from("operaciones_factoring_mora")
    .update({ estado: "pagada", fecha_pago_mora: fechaPagoMora })
    .eq("id", moraId)
    .select()
    .single();
  if (error) throw error;
  return data;
};

// factura_sii_recibida_id de las moras saldadas por NETEO (el DTE de mora se descuenta del giro
// de una operación de factoring posterior, sin débito bancario). FlujoCajaV2 las usa para
// excluirlas de "Por pagar", igual que las comisiones de factoring neteadas.
export const getFacturasMoraNeteada = async (pais) => {
  const { data, error } = await client
    .from("operaciones_factoring_mora")
    .select("factura_sii_recibida_id")
    .eq("pais", pais)
    .eq("estado", "saldada_neteo")
    .not("factura_sii_recibida_id", "is", null);
  if (error) throw error;
  return data || [];
};

// Todas las moras pendientes de un país (para FlujoCajaV2 → bloque Créditos).
export const getMorasPendientes = async (pais) => {
  const { data, error } = await client
    .from("operaciones_factoring_mora")
    .select(
      `
      *,
      operacion:operaciones_factoring!operaciones_factoring_mora_operacion_factoring_id_fkey(
        id, monto_factura, fecha_cobro_final,
        factura:facturas_sii_emitidas(folio, razon_social_receptor)
      )
    `
    )
    .eq("pais", pais)
    .eq("estado", "pendiente")
    .order("fecha_notificacion");
  if (error) throw error;
  return data || [];
};

// ── FASE 4 (dual-write) — espejo de liquidar/anular factoring al libro `aplicaciones_saldo` ──────
// El factoring salda facturas EMITIDAS sin movimiento bancario propio: evento via='factoring',
// origen='operacion_factoring', eje='cobro'. monto_documento = monto_total (liquida la factura
// entera, en su propia moneda → tc null). Best-effort, nunca silencioso (10.177/10.170).
export const construirEventosFactoring = ({ facturas = [], operacionId, fecha, usuarioId = null }) =>
  facturas
    .filter((f) => f && f.id != null && Number(f.monto_total) > 0)
    .map((f) => ({
      documento_tipo: "sii_emitida",
      documento_id: f.id,
      eje: "cobro",
      via: "factoring",
      origen_tipo: "operacion_factoring",
      origen_id: operacionId,
      cuota_id: null,
      monto_origen: Number(f.monto_total),
      moneda_origen: f.moneda || "CLP",
      tc: null,
      fecha_tc: null,
      monto_documento: Number(f.monto_total),
      fecha_evento: fecha,
      monto: Number(f.monto_total),
      created_by: usuarioId || null,
    }));

// Facturas emitidas de una operación (FK escalar + puente, deduplicadas), con total y moneda.
const getFacturasEmitidasDeOperacionParaLibro = async (sb, operacionId) => {
  const { data: op } = await sb
    .from("operaciones_factoring")
    .select("factura_sii_emitida_id")
    .eq("id", operacionId)
    .maybeSingle();
  const { data: puente } = await sb
    .from("operaciones_factoring_facturas")
    .select("factura_sii_emitida_id")
    .eq("operacion_factoring_id", operacionId);
  const ids = [
    ...new Set([op?.factura_sii_emitida_id, ...(puente || []).map((r) => r.factura_sii_emitida_id)].filter(Boolean)),
  ];
  if (ids.length === 0) return [];
  const { data: facs } = await sb.from("facturas_sii_emitidas").select("id, monto_total, moneda").in("id", ids);
  return facs || [];
};

/**
 * Liquida una operación de factoring de forma ATÓMICA vía el RPC
 * `liquidar_operacion_factoring` (ver migración 20260616_liquidar_operacion_factoring_rpc.sql).
 *
 * El RPC, en una sola transacción:
 *   1. UPDATE operaciones_factoring → estado='liquidada' + fecha_pago_real
 *   2. UPDATE facturas_sii_emitidas → estado_factoring='liquidada' + facturafechacobro
 *      en TODAS las facturas de la op (FK directo `factura_sii_emitida_id` + tabla puente
 *      `operaciones_factoring_facturas`, deduplicadas). Cubre ops multi-factura.
 *
 * NO crea movimientos_caja: el remanente se registra en caja al vincular el depósito
 * bancario real desde FlujoCajaV2 (Fase 2). Esto evita la escritura parcial que dejaba
 * el INSERT de caja al fallar por `registrado_por` NOT NULL.
 *
 * @param {object} operacion - debe traer al menos `id`.
 * @param {object} options
 * @param {string} [options.fechaPagoReal] - YYYY-MM-DD; default: hoy.
 */
export const liquidarOperacionFactoring = async (
  operacion,
  { fechaPagoReal, usuarioId, supabaseClient = client } = {}
) => {
  const fecha = fechaPagoReal || new Date().toISOString().split("T")[0];

  const { data, error } = await supabaseClient.rpc("liquidar_operacion_factoring", {
    p_op_id: operacion.id,
    p_fecha_pago_real: fecha,
  });
  if (error) throw error;

  // FASE 4 dual-write (best-effort — NO rompe la liquidación ya commiteada por el RPC): un evento
  // via='factoring' por cada factura de la operación.
  try {
    const facturas = await getFacturasEmitidasDeOperacionParaLibro(supabaseClient, operacion.id);
    const eventos = construirEventosFactoring({ facturas, operacionId: operacion.id, fecha, usuarioId });
    await escribirEventosSaldo(
      supabaseClient,
      { origen_tipo: "operacion_factoring", origen_id: operacion.id },
      eventos
    );
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(
      "[dual-write aplicaciones_saldo · factoring liquidar] best-effort, no rompe el flujo:",
      e?.message || e
    );
  }
  return data;
};

export const getOperacionFactoringPorFactura = async (facturaId) => {
  const { data, error } = await client
    .from("operaciones_factoring")
    .select("*, proveedor:proveedores_factoring(nombre, tipo)")
    .eq("factura_sii_emitida_id", facturaId)
    .in("estado", ["activa", "liquidada"])
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data || null;
};

/**
 * Crea una operación de factoring histórica (ya liquidada o activa sin pasar
 * por el flujo normal de cesión).
 *
 * Diferencias con crearOperacionFactoring:
 *  - Acepta estado: "activa" | "liquidada" | "anulada" directamente.
 *  - Acepta movimiento_bancario_adelanto_id y movimiento_bancario_remanente_id
 *    en el INSERT para vincular movimientos bancarios ya existentes.
 *  - Acepta giro_depositado (monto real depositado, puede diferir de giro_a_recibir).
 *  - Acepta referencia_proveedor (número de op del proveedor, ej: "406591").
 *  - NO modifica facturas_sii_emitidas — la factura puede no existir
 *    o ya tener el estado correcto.
 *  - factura_sii_emitida_id es opcional (puede ser null para ops muy antiguas).
 *
 * @param {object} operacion - Todos los campos de operaciones_factoring.
 *   Campos obligatorios: pais, proveedor_factoring_id, monto_factura,
 *     capital_adelantado, tasa, fecha_operacion.
 *   Campos opcionales adicionales: giro_depositado, referencia_proveedor,
 *     movimiento_bancario_adelanto_id, movimiento_bancario_remanente_id,
 *     factura_sii_emitida_id, fecha_cobro_final, fecha_pago_real.
 */
export const crearOperacionFactoringHistorica = async (operacion) => {
  const { data, error } = await client
    .from("operaciones_factoring")
    .insert({ ...operacion })
    .select()
    .single();
  if (error) throw error;
  return data;
};

// --- Comisiones de factoring: factura(s) SII recibida(s) del proveedor del factor ---
// La comisión se descuenta del giro (sin débito bancario); su DTE recibido quedaría huérfano
// en "Por pagar". Esta tabla puente N:1 lo vincula a la operación para excluirlo. Ver
// migración 20260609_operaciones_factoring_comisiones.sql.

// Trae las comisiones vinculadas a una operación, con embed de la factura recibida.
export const getComisionesFactoring = async (operacionId) => {
  const { data, error } = await client
    .from("operaciones_factoring_comisiones")
    .select(
      `id, factura_sii_recibida_id,
       factura:facturas_sii_recibidas(id, folio, fecha_emision, monto_total, razon_social_emisor)`
    )
    .eq("operacion_factoring_id", operacionId)
    .order("id");
  if (error) throw error;
  return data || [];
};

// Vincula una factura SII recibida de comisión a una operación (INSERT en la tabla puente).
export const vincularComisionFactoring = async (operacionId, facturaId) => {
  const { data, error } = await client
    .from("operaciones_factoring_comisiones")
    .insert({ operacion_factoring_id: operacionId, factura_sii_recibida_id: facturaId })
    .select()
    .single();
  if (error) throw error;
  return data;
};

// Desvincula una comisión (DELETE por id de la fila puente).
export const desvincularComisionFactoring = async (id) => {
  const { error } = await client.from("operaciones_factoring_comisiones").delete().eq("id", id);
  if (error) throw error;
};

// Facturas SII recibidas pendientes del RUT del proveedor de factoring, desde 2026-01-01, que
// NO estén ya vinculadas a ninguna operación. Candidatas para vincular como comisión.
export const getFacturasComisionDisponibles = async (pais, rutProveedor) => {
  if (!rutProveedor) return [];
  // IDs ya vinculados a cualquier operación → excluir.
  const { data: yaVinc, error: errVinc } = await client
    .from("operaciones_factoring_comisiones")
    .select("factura_sii_recibida_id");
  if (errVinc) throw errVinc;
  const excluidas = new Set((yaVinc || []).map((r) => r.factura_sii_recibida_id));

  const { data, error } = await client
    .from("facturas_sii_recibidas")
    .select("id, folio, fecha_emision, monto_total, razon_social_emisor, rut_emisor")
    .eq("pais", pais)
    .like("rut_emisor", `%${rutProveedor}%`)
    .gte("fecha_emision", "2026-01-01")
    .order("fecha_emision", { ascending: false });
  if (error) throw error;
  return (data || []).filter((f) => !excluidas.has(f.id));
};

export const actualizarProveedorFactoring = async (id, cambios) => {
  const { data, error } = await client
    .from("proveedores_factoring")
    .update({ ...cambios })
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;
  return data;
};

/**
 * Vincula N facturas SII a una operación de factoring existente.
 * Inserta en operaciones_factoring_facturas (tabla puente N:1) y
 * actualiza estado_factoring en cada factura_sii_emitida vinculada.
 *
 * @param {number} operacionId
 * @param {Array<{id: number, monto_total: number}>} facturas
 * @param {string} estadoFactoring - 'cedida' | 'liquidada'
 * @param {{fecha_pago_real?: string, fecha_cobro_final?: string, fecha_operacion?: string}} [operacionData] - solo
 *   se usa cuando estadoFactoring === 'liquidada' para propagar facturafechacobro a las facturas.
 */
export const vincularFacturasAOperacion = async (
  operacionId,
  facturas,
  estadoFactoring = "cedida",
  operacionData = null
) => {
  if (!facturas?.length) return;

  const rows = facturas.map((f) => ({
    operacion_factoring_id: operacionId,
    factura_sii_emitida_id: f.id,
    monto_factura: f.monto_total,
  }));

  const { error: errVinc } = await client.from("operaciones_factoring_facturas").upsert(rows, {
    onConflict: "operacion_factoring_id,factura_sii_emitida_id",
    ignoreDuplicates: false,
  });
  if (errVinc) throw errVinc;

  const ids = facturas.map((f) => f.id);
  const updateFactura = { estado_factoring: estadoFactoring };
  if (estadoFactoring === "liquidada") {
    updateFactura.facturafechacobro =
      operacionData?.fecha_pago_real || operacionData?.fecha_cobro_final || operacionData?.fecha_operacion || null;
  }
  const { error: errFacturas } = await client.from("facturas_sii_emitidas").update(updateFactura).in("id", ids);
  if (errFacturas) throw errFacturas;
};

/**
 * Vincula N movimientos bancarios a una operación de factoring.
 * Inserta en operaciones_factoring_movimientos. Para el primer mov
 * de cada tipo, también actualiza la FK legacy correspondiente para
 * mantener compatibilidad con código que aún lee directamente esos
 * campos (FlujoCajaV2 proyecciones, getFacturasYaVinculadas, etc.).
 *
 * @param {number} operacionId
 * @param {Array<{id: number, tipo: 'adelanto'|'remanente'}>} movimientos
 */
export const vincularMovimientosAOperacion = async (operacionId, movimientos) => {
  if (!movimientos?.length) return;

  const rows = movimientos.map((m) => ({
    operacion_factoring_id: operacionId,
    movimiento_bancario_id: m.id,
    tipo: m.tipo,
  }));

  const { error: errVinc } = await client.from("operaciones_factoring_movimientos").upsert(rows, {
    onConflict: "operacion_factoring_id,movimiento_bancario_id,tipo",
    ignoreDuplicates: false,
  });
  if (errVinc) throw errVinc;

  const primerAdelanto = movimientos.find((m) => m.tipo === "adelanto");
  const primerRemanente = movimientos.find((m) => m.tipo === "remanente");
  const legacyUpdate = {};
  if (primerAdelanto) legacyUpdate.movimiento_bancario_adelanto_id = primerAdelanto.id;
  if (primerRemanente) legacyUpdate.movimiento_bancario_remanente_id = primerRemanente.id;

  if (Object.keys(legacyUpdate).length > 0) {
    const { error: errLegacy } = await client.from("operaciones_factoring").update(legacyUpdate).eq("id", operacionId);
    if (errLegacy) throw errLegacy;
  }
};

/**
 * Carga los movimientos bancarios vinculados a una operación vía la
 * tabla puente. La tabla principal de Factoring usa el embed de
 * getOperacionesFactoring; este helper sirve para vistas on-demand.
 */
export const getMovimientosDeOperacion = async (operacionId) => {
  const { data, error } = await client
    .from("operaciones_factoring_movimientos")
    .select("id, tipo, movimiento:movimientos_bancarios(id, fecha, monto, descripcion_raw)")
    .eq("operacion_factoring_id", operacionId)
    .order("tipo")
    .order("id");
  if (error) throw error;
  return data || [];
};

/**
 * Carga las facturas vinculadas a una operación de factoring vía la
 * tabla puente. Útil para resolver una sola operación on-demand
 * (la tabla principal usa el embed de getOperacionesFactoring).
 */
export const getFacturasDeOperacion = async (operacionId) => {
  const { data, error } = await client
    .from("operaciones_factoring_facturas")
    .select(
      `id, monto_factura,
       factura:facturas_sii_emitidas(id, folio, razon_social_receptor, monto_total, fecha_emision)`
    )
    .eq("operacion_factoring_id", operacionId)
    .order("id");
  if (error) throw error;
  return data || [];
};
